import { useMutation, useQuery, useQueryClient } from "react-query";

import {
  createApiToken,
  createServiceAccount,
  deleteServiceAccount,
  listApiTokens,
  listPermissionCatalog,
  listServiceAccounts,
  revokeApiToken,
  updateServiceAccount,
} from "./api";
import type {
  CreateApiTokenInput,
  CreateServiceAccountInput,
  UpdateServiceAccountInput,
} from "./types";

const serviceAccountsKey = ["serviceAccounts"] as const;
const apiTokensKey = (serviceAccountId: string) =>
  ["serviceAccounts", serviceAccountId, "tokens"] as const;
const permissionCatalogKey = ["serviceAccounts", "permissionCatalog"] as const;

export function useServiceAccounts() {
  return useQuery(serviceAccountsKey, listServiceAccounts, {
    staleTime: 30_000,
  });
}

export function usePermissionCatalog() {
  return useQuery(permissionCatalogKey, listPermissionCatalog, {
    staleTime: Infinity,
  });
}

export function useCreateServiceAccount() {
  const queryClient = useQueryClient();
  return useMutation(
    (input: CreateServiceAccountInput) => createServiceAccount(input),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(serviceAccountsKey);
      },
    },
  );
}

export function useUpdateServiceAccount() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, input }: { id: string; input: UpdateServiceAccountInput }) =>
      updateServiceAccount(id, input),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(serviceAccountsKey);
      },
    },
  );
}

export function useDeleteServiceAccount() {
  const queryClient = useQueryClient();
  return useMutation((id: string) => deleteServiceAccount(id), {
    onSuccess: () => {
      void queryClient.invalidateQueries(serviceAccountsKey);
    },
  });
}

export function useApiTokens(serviceAccountId: string | null) {
  return useQuery(
    apiTokensKey(serviceAccountId ?? ""),
    () => listApiTokens(serviceAccountId as string),
    { enabled: !!serviceAccountId, staleTime: 30_000 },
  );
}

export function useCreateApiToken(serviceAccountId: string) {
  const queryClient = useQueryClient();
  return useMutation(
    (input: CreateApiTokenInput) => createApiToken(serviceAccountId, input),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(apiTokensKey(serviceAccountId));
        void queryClient.invalidateQueries(serviceAccountsKey);
      },
    },
  );
}

export function useRevokeApiToken(serviceAccountId: string) {
  const queryClient = useQueryClient();
  return useMutation(
    (apiTokenId: string) => revokeApiToken(serviceAccountId, apiTokenId),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(apiTokensKey(serviceAccountId));
      },
    },
  );
}
